import React from 'react';

import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ChecklistIcon from '@mui/icons-material/Checklist';
import {
  Box,
  Chip,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';

import SalesforceLinkButton from '../../shared/SalesforceLinkButton';

import InclusionsPanel from './InclusionsPanel';
import { BUNDLED_EXPERIENCE_LISTED_IN_COLUMN_MIN_WIDTH } from './constants';
import { formatDealOptionCurrency, formatDealOptionValue } from './dealOptionsFormatters';
import type { DealOptionRow } from './dealOptionsModel';

export type DealOptionDetailTab = 'inclusions' | 'bundled-experiences';

interface Props {
  activeTab: DealOptionDetailTab;
  onActiveTabChange: (tab: DealOptionDetailTab) => void;
  row: DealOptionRow;
}

const DETAIL_TABS: Array<{ icon: React.ReactElement; label: string; value: DealOptionDetailTab }> = [
  { icon: <ChecklistIcon fontSize="small" />, label: 'Inclusions', value: 'inclusions' },
  { icon: <CardGiftcardIcon fontSize="small" />, label: 'Bundled Experiences', value: 'bundled-experiences' },
];

function getDetailTabCount(tab: DealOptionDetailTab, row: DealOptionRow) {
  if (tab === 'inclusions') {
    return row.inclusions.length;
  }

  return row.bundledExperiences.length;
}

function renderBundledExperiencesPanel(row: DealOptionRow) {
  if (row.bundledExperiences.length === 0) {
    return <Typography color="text.secondary">No bundled experiences are linked to this deal option.</Typography>;
  }

  return (
    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: BUNDLED_EXPERIENCE_LISTED_IN_COLUMN_MIN_WIDTH }}>Listed In</TableCell>
          <TableCell>Account Name</TableCell>
          <TableCell>Deal Option Name</TableCell>
          <TableCell>Fare Type</TableCell>
          <TableCell>Sub Category</TableCell>
          <TableCell align="right" sx={{ width: 100 }}>
            Ancillary Sell price uplift
          </TableCell>
          <TableCell align="right">Value (cost currency)</TableCell>
          <TableCell>Currency</TableCell>
          <TableCell>Destination</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {row.bundledExperiences.map((experience) => (
          <TableRow key={experience.id}>
            <TableCell sx={{ width: BUNDLED_EXPERIENCE_LISTED_IN_COLUMN_MIN_WIDTH }}>
              <SalesforceLinkButton value={experience.listedInLinkHtml} />
            </TableCell>
            <TableCell>{formatDealOptionValue(experience.accountName)}</TableCell>
            <TableCell>{formatDealOptionValue(experience.dealOptionName)}</TableCell>
            <TableCell>{formatDealOptionValue(experience.fareType)}</TableCell>
            <TableCell>{formatDealOptionValue(experience.subCategory)}</TableCell>
            <TableCell align="right">{formatDealOptionValue(experience.ancillarySellPriceUplift)}</TableCell>
            <TableCell align="right">{formatDealOptionCurrency(experience.currency, experience.value)}</TableCell>
            <TableCell>{formatDealOptionValue(experience.currency)}</TableCell>
            <TableCell>{formatDealOptionValue(experience.destination)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function DealOptionDetails({ activeTab, onActiveTabChange, row }: Props) {
  return (
    <Box sx={{ bgcolor: 'grey.50', px: 3, py: 2 }}>
      <Tabs
        value={activeTab}
        variant="scrollable"
        onChange={(_event, value: DealOptionDetailTab) => onActiveTabChange(value)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 40,
          '& .MuiTab-root': { minHeight: 40, textTransform: 'none' },
        }}
      >
        {DETAIL_TABS.map((tab) => (
          <Tab
            key={tab.value}
            icon={tab.icon}
            iconPosition="start"
            label={
              <Stack alignItems="center" direction="row">
                {tab.label}
                <Chip label={getDetailTabCount(tab.value, row)} size="small" sx={{ height: 20, ml: 0.75 }} />
              </Stack>
            }
            value={tab.value}
          />
        ))}
      </Tabs>

      <Box sx={{ pt: 2 }}>
        {activeTab === 'inclusions' &&
          (row.inclusions.length === 0 ? (
            <Typography color="text.secondary">No inclusions are linked to this deal option.</Typography>
          ) : (
            <InclusionsPanel inclusions={row.inclusions} />
          ))}
        {activeTab === 'bundled-experiences' && renderBundledExperiencesPanel(row)}
      </Box>
    </Box>
  );
}
